import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { useDocsOrgs, type DocsOrg } from '@/api/docs';
import { Chip, Empty, ListPage, PageLoading, Th, tableWrap, theadCls, useListState, type FilterDef } from '@/components/network/NetworkKit';
import { OrgCompany } from '@/components/docs/DocsKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { docsOrgPath } from '@/lib/routes';

/**
 * IT Glue organisations — each linked to its Autotask company by IT Glue's own PSA integration
 * (no mapping in Cosmos). Click through for documents, configurations, contacts and expirations.
 */

const FILTERS: FilterDef<DocsOrg>[] = [
  { id: 'active',   label: 'Active',             test: (o) => o.status !== 'Inactive' },
  { id: 'all',      label: 'All',                test: () => true },
  { id: 'unlinked', label: 'Not linked to Autotask', test: (o) => o.autotaskCompanyId == null },
  { id: 'expiring', label: 'Expiring in 90 days', test: (o) => o.expiringSoon > 0 },
  { id: 'alert',    label: 'With an alert',      test: (o) => !!o.alert },
];

export default function DocsOrganizationsPage() {
  const { data, isLoading, error, isFetching } = useDocsOrgs({ scoped: true });
  const navigate = useNavigate();
  const items = data?.items ?? [];
  const l = useListState('cosmos:docs:orgs:show', FILTERS, items, (o) => `${o.name} ${o.shortName ?? ''} ${o.quickNotesPreview ?? ''}`);

  return (
    <ListPage area="Docs" title="Organisations" count={data ? l.rows.length : null} isFetching={isFetching}
      subtitle={data && <>IT Glue · linked to Autotask by IT Glue's PSA integration · read {fmtRelative(data.fetchedAt)}</>}
      search={{ q: l.q, setQ: l.setQ, placeholder: 'Organisation or quick notes' }}
      filters={{ defs: FILTERS, show: l.show, pick: l.pick, counts: l.counts }}>
      <PageLoading isLoading={isLoading} error={error} vendor="IT Glue" />
      {data && (
        <div className={tableWrap}>
          <table className="w-full text-sm" style={{ minWidth: 1000 }}>
            <thead className={theadCls}>
              <tr><Th>Organisation</Th><Th>Autotask company</Th><Th>Type</Th><Th right>Configurations</Th><Th right>Expiring (90d)</Th><Th>Quick notes</Th><Th>Updated</Th><Th /></tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {l.rows.length === 0 && <Empty cols={8} />}
              {l.rows.map((o) => (
                <tr key={o.id} className="cursor-pointer hover:bg-muted/30" onClick={() => navigate(docsOrgPath(o.id))}>
                  <td className="min-w-[200px] px-3 py-2">
                    <div className="font-medium text-foreground">{o.name}</div>
                    {o.alert && <div className="mt-0.5"><Chip tone="warn" title={o.alert}>Alert</Chip></div>}
                  </td>
                  <td className="px-3 py-2 text-xs" onClick={(e) => e.stopPropagation()}><div className="max-w-[200px]"><OrgCompany org={o} /></div></td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{o.type ?? '—'}{o.status === 'Inactive' && <span className="ml-1 italic">· inactive</span>}</td>
                  <td className="px-3 py-2 text-right text-xs tabular-nums">{o.configurations}</td>
                  <td className="px-3 py-2 text-right text-xs tabular-nums">{o.expiringSoon ? <span className="font-medium text-warning">{o.expiringSoon}</span> : <span className="text-muted-foreground">0</span>}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground"><div className="max-w-[200px] truncate" title={o.quickNotesPreview ?? ''}>{o.quickNotesPreview ?? '—'}</div></td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">{o.updatedAt ? fmtRelative(o.updatedAt).replace(/^(about|almost|over) /, '') : '—'}</td>
                  <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    {o.url && <a href={o.url} target="_blank" rel="noreferrer" title="Open in IT Glue" className="text-muted-foreground hover:text-foreground"><ExternalLink className="h-3.5 w-3.5" /></a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ListPage>
  );
}
